import {Injectable, NgZone} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {BehaviorSubject, map, Observable, startWith, Subject, Subscription} from 'rxjs';
import {combineLatest, filter, withLatestFrom} from 'rxjs/operators';

import {FeatureGroup, LatLng, LatLngBounds, LatLngExpression, Map, Popup} from 'leaflet';

import {mapTypeEventTypes} from '../../../consts/mouse-type-events';
import {MapEvent} from '../../../models/map/map-event.model';
import {Cluster} from '../../../models/map/Cluster';
import {Vehicle} from '../../../models/domain/Vehicle';

import {ClusterDrawerService} from '../drawer/cluster/cluster-drawer.service';
import {PopupRendererService} from '../renderer/popup-renderer.service';
import {ClusterService} from '../cluster/cluster.service';
import {MappingService} from '../../mapping/mapping.service';
import {VehicleService} from '../../vehicle/vehicle.service';

import {Consts} from '../../../consts/consts';
import {MapLayers, MapLayerType} from '@src/app/map/map-layers';

import {MapPanelService} from "@map-panel/services/map-panel.service";

import {MapEventsService} from "@commons/services/map/map-events.service";
import {MapConfigService} from "@commons/services/map/config/map-config.service";

import {SettingsService} from "@shared/services/settings.service";
import {JavascriptInjectorService} from "@shared/services/external-scripts/javascript-injector.service";
import {AuthenticationService} from "@shared/services/authentication/authentication.service";

import {VehicleUIEventsService} from "@layout/vehicle/services/vehicle-uievents.service";
import { RouteEvent } from '@commons/models/domain/RouteEvent';
import { BusinessEvent } from '@commons/models/domain/BusinessEvent';
import { EventTypeEnum } from '@commons/enums/event-type.enum';

@Injectable()
export class MapService {

  public onClickParkStopIcon$: Subject<RouteEvent | null> = new Subject();
  public onClickEventList$: Subject<{ data: BusinessEvent | null, eventMode: EventTypeEnum }> = new Subject();
  public isVisibleVehicleList$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly mapLayers = new MapLayers();

  private firstLoad = false;
  private popup?: Popup;
  private popupTimeout: any;
  private lastCords?: LatLng;

  private mapEventSubject = new Subject<MapEvent>();
  private centerMapRequestSubject = new Subject<boolean>();
  private movementEndSubject = new Subject<{ zoom: number, bounds: LatLngBounds, source?: string }>();

  private centerMapRequestSub?: Subscription;
  private clustersSub?: Subscription;
  private mapEventSub?: Subscription;
  private markerEventsSub?: Subscription;

  private isRoutesTab = false;
  private googleMapScriptsLoaded = false;
  private mainMapZoomLevelOffset = -3;
  private selectedVehicleZoom = 0;
  private isVehicleSelected = false;
  private mapReadySub$ = new BehaviorSubject(false);

  public readonly isVisibleMapLoader$ = new BehaviorSubject(false);

  map?: Map;
  /**
   * Podział mapy na poszczególne warstwy.
   * Do tego przydały się potem jakiś mapper coby filtrować od razu co się ma wyświetlić a co nie.
   */
  groups: any = {
    clusters: new FeatureGroup(),
    markers: new FeatureGroup(),
    popups: new FeatureGroup(),
    events: new FeatureGroup(),
    routes: new FeatureGroup(),
    areas: new FeatureGroup()
  };

  get mapEvent$(): Observable<MapEvent> {
    return this.mapEventSubject.asObservable();
  }

  private _currentMapLayerType = MapLayerType.none;
  get currentMapLayerType(): MapLayerType {
    return this._currentMapLayerType;
  }

  private mapLayerChangedSubject = new Subject<MapLayerType>();

  get mapLayerChanged(): Observable<MapLayerType> {
    return this.mapLayerChangedSubject.asObservable();
  }

  get mapReady$(): Observable<boolean> {
    return this.mapReadySub$.asObservable();
  }

  constructor(
    private popupRendererService: PopupRendererService,
    private vehicleService: VehicleService,
    private clusterService: ClusterService,
    private mappingService: MappingService,
    private mapEventsService: MapEventsService,
    private clusterDrawerService: ClusterDrawerService,
    private ngZone: NgZone,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private settingsService: SettingsService,
    private mapPanelService: MapPanelService,
    private javascriptInjectorService: JavascriptInjectorService,
    private authenticationService: AuthenticationService,
    private vehicleUIEventsService: VehicleUIEventsService,
    private mapConfigService: MapConfigService
  ) {
    this.subToGetMarkerEventsStream();
    this.subToMapLayerChanged();
    this.subToIsRoutesTab();
    this.subToChangeMinimapZoom();
    this.subToCurrentVehicleIdChange();
  }

  onMapReady(map: Map) {
    this.map = map;

    this.bindEvents();
    this.bindMapWithLayerGroups();
    this.initStreams();
    this.setSavedMapLayer();

    this.selectedVehicleZoom = this.map!.getMaxZoom() + this.mainMapZoomLevelOffset;
    this.subToMapZoomend();

    this.mapReadySub$.next(true);
  }

  public setMapLoaderState(isVisible: boolean): void {
    this.isVisibleMapLoader$.next(isVisible);
  }

  changeZoom(interval: number): void {
    this.map?.setZoom(this.map.getZoom() + interval);
  }

  centerMap(): void {
    this.centerMapRequestSubject.next(true);
  }

  unsubscribeAllStreams(): void {
    if (this.mapEventSub) {
      this.mapEventSub.unsubscribe();
    }
    if (this.clustersSub) {
      this.clustersSub.unsubscribe();
    }
    if (this.centerMapRequestSub) {
      this.centerMapRequestSub.unsubscribe();
    }
  }

  showPointWithZoom(coords: LatLng, zoom = this.selectedVehicleZoom) {
    this.lastCords = coords;
    this.map?.setView(coords, zoom);
    setTimeout(() => {
      this.map?.setZoom(zoom);
    }, 1000);
  }

  resizeMap() {
    setTimeout(() => {
      this.map?.invalidateSize();
    }, 0);
  }

  setMapLayer(mapLayerType: MapLayerType) {
    if (this.map == null)
      return;

    this._currentMapLayerType = mapLayerType;
    this.map.eachLayer(x => this.map!.removeLayer(x));
    this.addMapLayer(this._currentMapLayerType);
    this.settingsService.setMapLayer(this._currentMapLayerType);
    this.bindMapWithLayerGroups();
    this.mapPanelService.forceDrawRouteSubject.next(true);
    this.mapLayerChangedSubject.next(this._currentMapLayerType);
  }

  fitMapBoundsToVehicles(vehicles: Vehicle[]): void {
    // Return if empty list of vehicles
    if (vehicles.length === 0) {
      return;
    }

    // Extract data - to do - change to one run
    const lats = [];
    const lngs = [];

    for (let i = vehicles.length - 1; i >= 0; i--) {
      const v = vehicles[i];

      if (v.LL) {
        const lat = parseFloat('' + v.LL.Lt);
        const lng = parseFloat('' + v.LL.Ln);

        if (lat && lat !== 361) {
          lats.push(lat);
        }
        if (lng && lng !== 361) {
          lngs.push(lng);
        }
      }
    }

    this.fitMapToBounds(lats, lngs);
  }

  private subToMapZoomend() {
    this.map!.on('zoomend', () => {
      if (this.isVehicleSelected) {
        this.selectedVehicleZoom = this.map?.getZoom()!;
      }
    });
  }

  private subToCurrentVehicleIdChange() {
    this.mapConfigService.currentVehicleIdChange().subscribe(data => {
      this.isVehicleSelected = !isNaN(data.currentVehicleId);
    });
  }

  private subToGetMarkerEventsStream() {
    // marker event handler
    this.markerEventsSub = this.clusterDrawerService.getMarkerEventsStream().subscribe(result => {
      if (result == null) {
        return;
      }
      this.ngZone.run(() => {
        switch (result.event) {
          case 'click':
            this.markerClick(result);
            break;

          case 'mouseover':
            this.markerMouseOver(result);
            break;

          case 'mouseout':
            this.markerMouseOut(result);
            break;
        }
      });
    });
  }

  private markerClick(result: any) {
    // pojazd
    if (result.type === 'vehicle') {
      // get cluster
      const clusterId = result.clusterId;
      const cluster = (<any>window).CLUSTERS[clusterId];
      if (cluster) {
        // przesuniecie na mapie
        const coords = new LatLng(cluster.lat, cluster.lng);
        this.map?.setView(coords, this.selectedVehicleZoom);
        this.mapEventsService.emmitVehicleClicked(cluster.properties.Id);
      }
    }

    // groupa
    if (result.type === 'group') {
      // get cluster
      const clusterId = result.clusterId;
      const cluster = (<any>window).CLUSTERS[clusterId];
      if (cluster) {
        // schowanie popup
        this.groups.popups.eachLayer((l: any) => {
          const _l = <any>l;
          if (_l.options.clusterId === clusterId) {
            this.groups.popups.removeLayer(l);
            l.remove();
          }
        });

        // pobranie wszystko elementow w klastrze z rozszerzenie do maximum
        const latlngs = this.getAllChildren(cluster.clusterId);
        let latMin = 0;
        let latMax = 0;
        let lngMin = 0;
        let lngMax = 0;
        for (const l of latlngs) {
          if (!latMin) {
            latMin = l.lat;
          }
          if (!latMax) {
            latMax = l.lat;
          }
          if (!lngMin) {
            lngMin = l.lng;
          }
          if (!lngMax) {
            lngMax = l.lng;
          }
          latMin = Math.min(latMin, l.lat);
          latMax = Math.max(latMax, l.lat);
          lngMin = Math.min(lngMin, l.lng);
          lngMax = Math.max(lngMax, l.lng);
        }
        this.map?.fitBounds([[latMin, lngMin], [latMax, lngMax]], Consts.MapPadding);
      }
    }
  }

  private markerMouseOver(result: any) {
    let className = 'popupCustom';
    if (result.type === 'group') {
      className += ' popupCustomGroup';
    } else {
      className += ` popupCustomVehicle popup-vehicle${ result.clusterId }`;
    }

    const _Popup = Popup.extend({
      options: {
        clusterId: undefined, isVisible: undefined, isOpenByEvent: undefined
      }
    });

    // get cluster
    const clusterId = result.clusterId;
    const cluster = (<any>window).CLUSTERS[clusterId];
    if (cluster) {
      // check if exists
      let alreadyExists = false;
      this.groups.popups.eachLayer((l: any) => {
        const _l = <any>l;
        if (_l.options.clusterId === clusterId) {
          alreadyExists = true;
        }

        if (_l.options.clusterId !== clusterId && _l.options.isOpenByEvent === true) {
          this.groups.popups.removeLayer(l);
          l.remove();
        }
      });

      // if exists
      if (alreadyExists) {
        return;
      }

      if (this.popupTimeout) {
        clearTimeout(this.popupTimeout);
      }

      // pokazanie popup
      this.popupTimeout = setTimeout(() => {

        this.popup = new _Popup();
        this.popup!.options.className = className;
        this.popup!.options.closeButton = false;
        (this.popup as any).options.clusterId = clusterId;
        (this.popup as any).options.isVisible = true;
        (this.popup as any).options.isOpenByEvent = true;
        this.popup!.options.keepInView = false;
        this.popup!.options.autoPan = false;

        const _html = this.popupRendererService.renderHtmlForPopup(cluster);
        this.popup!.setLatLng(this.getAdjustedCoordsForPopup([cluster.lat, cluster.lng], cluster));
        this.popup!.setContent(_html);
        this.popup!.addTo(this.groups.popups);

        // dodanie
        const mouseover = () => {
          if (this.popupTimeout) {
            clearTimeout(this.popupTimeout);
          }
        };
        const mouseout = () => {
          this.popupTimeout = setTimeout(() => {
            this.groups.popups.eachLayer((l: any) => {
              const _l = <any>l;
              if (_l.options.clusterId === clusterId && _l.options.isOpenByEvent === true) {
                this.groups.popups.removeLayer(l);
                l.remove();
              }
            });
          }, 400);
        };

        // otwarcie okna z detalami pojazd
        const openVehicleFromPopup = (e: any) => {
          const el = e.target;
          const id = el.getAttribute('data-vehicle-id');
          if (id) {
            this.mapEventsService.emmitVehicleClicked(id);

          }
        };
        // dodanie do popoup zdarzen
        const __el = this.popup?.getElement();
        __el!.removeEventListener('mouseover', mouseover);
        __el!.addEventListener('mouseover', mouseover);
        __el!.removeEventListener('mouseout', mouseout);
        __el!.addEventListener('mouseout', mouseout);
        __el!.removeEventListener('click', openVehicleFromPopup);
        __el!.addEventListener('click', openVehicleFromPopup);

      }, 300);
    }
  }

  private markerMouseOut(result: any) {
    {
      if (this.popupTimeout) {
        clearTimeout(this.popupTimeout);
      }
      this.popupTimeout = setTimeout(() => {
        if (this.popup) {
          const clusterId = result.clusterId;
          this.groups.popups.eachLayer((l: any) => {
            const _l = <any>l;
            if (_l.options.clusterId === clusterId && _l.options.isOpenByEvent === true) {
              this.groups.popups.removeLayer(l);
              l.remove();
            }
          });
        }
      }, 100);
    }
  }

  /**
   * pobranie wszystkich elementów w klastrze
   */
  private getAllChildren(clusterId: number, children?: Array<LatLng>): Array<LatLng> {
    if (!children) {
      children = new Array<LatLng>();
    }
    if (clusterId) {
      const points = this.clusterService.getChildren(clusterId);
      for (const c of points) {

        const lat = c.lat;
        const lng = c.lng;

        children.push(new LatLng(lat, lng));

        if (c.properties.cluster_id) {
          children = this.getAllChildren(c.properties.cluster_id, children);
        }
      }
    }

    return children;
  }

  private setSavedMapLayer() {
    const savedMapLayer = this.settingsService.getMapLayer();
    if (savedMapLayer)
      this.setMapLayer(savedMapLayer);
    else {
      this.setMapLayer(MapLayerType.osm);
      setTimeout(() => this.mapPanelService.isMapPanelVisibleSubject.next(true), 1000);
    }
  }

  private bindEvents(): void {
    for (const mapEventType of mapTypeEventTypes) {
      this.map?.on(mapEventType, (event) => {
        this.mapEventSubject.next({mapEventType, event} as MapEvent);
      });
    }
  }

  private bindMapWithLayerGroups(): void {
    for (const group_name of Object.keys(this.groups)) {
      const group = this.groups[group_name];
      group.clearLayers();
      group.addTo(this.map);
    }
  }

  private initStreams(): void {
    const $filtered = this.vehicleService.getListFiltered$();
    // Zbudowanie strumienia klastrów + subskrybcja i ich narysowanie
    this.map?.setZoom(this.map.getMinZoom() + 3);
    this.clustersSub = this.movementEndSubject.asObservable().pipe(
      // 1. 'Podrobienie' zdarzenia końca ruchu mapy, aby cały strumień ruszył.
      startWith({zoom: this.map?.getZoom(), bounds: this.map?.getBounds()}),
      // 2. Połączenie strumienia zdarzeń końca ruchów mapy i strumienia lokalizacji przefiltrowanych pojazdów.
      //    Jeśli jeden coś przesyła to z drugiego bierzemy ostatnią wartość i obie wartości strumieniujemy dalej.
      combineLatest(
        // 3. Ale najpierw mapowanie strumienia samochodów w strumień ich lokalizacji.
        $filtered.pipe(map(vehicles => this.mappingService.mapVehiclesToPoints(vehicles))),
        // 4. Skoro łączymy dwa strumienie w jeden (parametry mapy, lokaliazje pojazdów)
        // to na tym etapie dostajemy parametry mapy i punkty.
        (params, points) => {
          // 5. Klastrowanie tylko wtedy gdy grupa klastrów dodana do mapy.
          if (this.map?.hasLayer(this.groups.clusters)) {
            // 6. Zbudowanie kd-drzewa do klastrowania + obliczenie i zwrócenie klastrów.
            this.clusterService.load(points);
            return this.clusterService.getClusters(params.bounds!, Math.floor(params.zoom!));
          }
          return [];
        }))
      // 7. Subskrybcja i narysowanie klastrów.
      .subscribe((clusters: any) => {
        // Zapisanie w window
        (<any>window).CLUSTERS = {};
        for (const cluster of clusters) {
          (<any>window).CLUSTERS[cluster.getClusterPK()] = cluster;
        }
        // stare markery
        const _oldGroup = this.groups.markers;
        // nowe markery
        const _newGroup = this.clusterDrawerService.draw(clusters, this.map!);
        // aktualizacj apokazanych popup
        this.groups.popups.eachLayer((_p: any) => {
          const p = <any>_p;
          // wyłączenie renderowania zawartości popup jak jest grupa
          if (p.options.isOpenByEvent === true && p.options.clusterId >= 0) {
            return;
          }
          const clusterId = p.options.clusterId;
          const cluster = (<any>window).CLUSTERS[clusterId];
          if (cluster) {
            const coords = new LatLng(cluster.lat, cluster.lng);
            p.setLatLng(this.getAdjustedCoordsForPopup([cluster.lat, cluster.lng], cluster));
            const content = this.popupRendererService.renderHtmlForPopup(cluster);
            p.setContent(content);
          }
        });

        // aktualizacja grup
        this.map?.removeLayer(this.groups.clusters);
        this.groups.clusters = _newGroup;
        this.groups.clusters.addTo(this.map);
        // Pierwsze zblizenie po zaladowaniu
        if (!this.firstLoad && clusters.length > 0) {
          // sprawdzenie czy zakladni nie sa otwarte
          const url = (<any>this.activatedRoute.snapshot)._routerState.url;
          if (url.indexOf('/panel/vehicle') === -1) {
            setTimeout(() => {
              this.centerMap();
              this.firstLoad = true;
            }, 1000);
          }
        } else {
          // narysowanie popupow jak sa wlaczone
          this.popupRendererService.renderPopups(this.groups);
        }
      });

    // Zbudowanie strumienia zdarzeń centrowania mapy
    // 1. Poczekaj aż pobrana zostanie pierwsza lista przefiltrowanych pojazdów aby było na czym centrować
    this.centerMapRequestSub =
      // 2. Następnie stwórz strumień żądań wycentrowania mapy + 'podrób'
      // kliknięcie użytkownika aby wycentrować przy pierwszym pokazaniu mapy
      this.centerMapRequestSubject.pipe(
        filter(r => r),
        // 3. Do informacji o kliknięciu potrzebna jest też ostatnia lista przefiltrowanych pojazdów
        withLatestFrom($filtered, (request, vehicles) => vehicles))
        // 4. Subskrybcja na strumień + wykonanie centrowania mapy na otrzymanych pojazdach
        .subscribe((vehicles: any) => {
          if (vehicles && vehicles.length > 0) {
            this.fitMapBoundsToVehicles(vehicles);
          }
        });

    // Subskrypcja strumienia zdarzeń mapy i multiplexing zdarzeń do odpowiednich funkcji
    this.mapEventSub = this.mapEventSubject.subscribe(mapEvent => {
      const mapEventType = mapEvent.mapEventType;
      switch (mapEventType) {
        case 'moveend':
          this.onMovementEnd(mapEvent.event);
          break;
      }
    });
  }

  private fitMapToBounds(lats: Array<number>, lngs: Array<number>) {
    // Find extrema
    let latMin = 0;
    let latMax = 0;
    let lngMin = 0;
    let lngMax = 0;

    lats.forEach(o => {
      if (o) {
        if (!latMin) {
          latMin = o;
        }
        if (!latMax) {
          latMax = o;
        }

        if (o < latMin) {
          latMin = o;
        }
        if (o > latMax) {
          latMax = o;
        }
      }
    });

    lngs.forEach(o => {
      if (o) {
        if (!lngMin) {
          lngMin = o;
        }
        if (!lngMax) {
          lngMax = o;
        }

        if (o < lngMin) {
          lngMin = o;
        }
        if (o > lngMax) {
          lngMax = o;
        }
      }
    });

    // Prepare bounds
    if (latMin && latMax && lngMin && lngMax) {
      const bounds = new LatLngBounds(
        [latMin, lngMin],
        [latMax, lngMax]
      );

      this.map?.fitBounds(bounds, Consts.MapPadding);
    }
  }

  private onMovementEnd(event: any): void {

    const ts = new Date();
    const params = {
      'zoom': this.map!.getZoom(),
      'bounds': this.map!.getBounds(),
      'source': 'onMovementEnd',
      'ts': `${ts.getMinutes()}:${ts.getSeconds()}:${ts.getMilliseconds()}`
    };

    this.movementEndSubject.next(params);
  }

  private getAdjustedCoordsForPopup(latlng: LatLngExpression, cluster: Cluster): LatLngExpression {
    const containerWidth = 190; //z src\app\app.map.map-controls.vehicle-location.vehicle-list-lite.vehicle-list-lite-item.vehicle-list-full.vehicle-list-full-item.vehicle-list-full-tacho-header.vehicle-list-full-indicator-value-header.vehicle-list-full-indicator-status-header.vehicle-list-full-inidicator-column-full.vehicle-list-full-expand.vehicle-list-full-config.vehicle-list-filter.vehicle-details.vehicle-details-tabs.vehicle-details-tacho-tab.vehicle-details-routes-tab.vehicle-details-route-item.vehicle-details-route-stats.vehicle-details-params-tab.vehicle-details-eco-tab.vehicle-details-etoll-balance-tab.vehicle-details-header.vehicle-signal-lost.vehicle-last-updated.vehicle-current-driver.vehicle-control-indicator-value.vehicle-control-indicator-signal-value.vehicle-control-indicator-value-type.survey.profile-data-update-info-list.profile-data-update-info-details.profile-data-logged-user.profile-data-help-info.profile-data-details.common-placeholder.password-reset-confirm.password-reset.password-change.panel.notification.event-list-lite.event-list.event-list-item-info.event-list-fueI-item.event-list-area-item.event-list-alarm-item.event-list-filter.driver-list-lite.driver-list-lite-item.driver-list-lite-item-current-vehicel.driver-list-filter.driver-details.driver-details-tabs.driver-details-tacho-tab.driver-tacho-slot-details.driver-details-sms-tab.driver-details-sms-send.driver-details-sms-search.driver-details-sms-list.driver-details-eco-tab.driver-details-header.bok.auto-login.select-database.login.area-list-lite.area-list-item.area-form.area-form-input.area-list-filter.area-delete.area-create.time.tag-picker.select.search.scrolled-list.scrolled-bar.form-field.double-date-picker.date-picker.checkbox.button.component.less, .popupCustom
    const maxContainerHeight = 128; //z src\app\app.map.map-controls.vehicle-location.vehicle-list-lite.vehicle-list-lite-item.vehicle-list-full.vehicle-list-full-item.vehicle-list-full-tacho-header.vehicle-list-full-indicator-value-header.vehicle-list-full-indicator-status-header.vehicle-list-full-inidicator-column-full.vehicle-list-full-expand.vehicle-list-full-config.vehicle-list-filter.vehicle-details.vehicle-details-tabs.vehicle-details-tacho-tab.vehicle-details-routes-tab.vehicle-details-route-item.vehicle-details-route-stats.vehicle-details-params-tab.vehicle-details-eco-tab.vehicle-details-etoll-balance-tab.vehicle-details-header.vehicle-signal-lost.vehicle-last-updated.vehicle-current-driver.vehicle-control-indicator-value.vehicle-control-indicator-signal-value.vehicle-control-indicator-value-type.survey.profile-data-update-info-list.profile-data-update-info-details.profile-data-logged-user.profile-data-help-info.profile-data-details.common-placeholder.password-reset-confirm.password-reset.password-change.panel.notification.event-list-lite.event-list.event-list-item-info.event-list-fueI-item.event-list-area-item.event-list-alarm-item.event-list-filter.driver-list-lite.driver-list-lite-item.driver-list-lite-item-current-vehicel.driver-list-filter.driver-details.driver-details-tabs.driver-details-tacho-tab.driver-tacho-slot-details.driver-details-sms-tab.driver-details-sms-send.driver-details-sms-search.driver-details-sms-list.driver-details-eco-tab.driver-details-header.bok.auto-login.select-database.login.area-list-lite.area-list-item.area-form.area-form-input.area-list-filter.area-delete.area-create.time.tag-picker.select.search.scrolled-list.scrolled-bar.form-field.double-date-picker.date-picker.checkbox.button.component.less, .popupCustom
    const itemHeight = 32; //z src\app\app.map.map-controls.vehicle-location.vehicle-list-lite.vehicle-list-lite-item.vehicle-list-full.vehicle-list-full-item.vehicle-list-full-tacho-header.vehicle-list-full-indicator-value-header.vehicle-list-full-indicator-status-header.vehicle-list-full-inidicator-column-full.vehicle-list-full-expand.vehicle-list-full-config.vehicle-list-filter.vehicle-details.vehicle-details-tabs.vehicle-details-tacho-tab.vehicle-details-routes-tab.vehicle-details-route-item.vehicle-details-route-stats.vehicle-details-params-tab.vehicle-details-eco-tab.vehicle-details-etoll-balance-tab.vehicle-details-header.vehicle-signal-lost.vehicle-last-updated.vehicle-current-driver.vehicle-control-indicator-value.vehicle-control-indicator-signal-value.vehicle-control-indicator-value-type.survey.profile-data-update-info-list.profile-data-update-info-details.profile-data-logged-user.profile-data-help-info.profile-data-details.common-placeholder.password-reset-confirm.password-reset.password-change.panel.notification.event-list-lite.event-list.event-list-item-info.event-list-fueI-item.event-list-area-item.event-list-alarm-item.event-list-filter.driver-list-lite.driver-list-lite-item.driver-list-lite-item-current-vehicel.driver-list-filter.driver-details.driver-details-tabs.driver-details-tacho-tab.driver-tacho-slot-details.driver-details-sms-tab.driver-details-sms-send.driver-details-sms-search.driver-details-sms-list.driver-details-eco-tab.driver-details-header.bok.auto-login.select-database.login.area-list-lite.area-list-item.area-form.area-form-input.area-list-filter.area-delete.area-create.time.tag-picker.select.search.scrolled-list.scrolled-bar.form-field.double-date-picker.date-picker.checkbox.button.component.less, .popupCustom
    const border = 16; //offset dla bordera (z obu stron, tj. 8px z jednej), z src\app\app.map.map-controls.vehicle-location.vehicle-list-lite.vehicle-list-lite-item.vehicle-list-full.vehicle-list-full-item.vehicle-list-full-tacho-header.vehicle-list-full-indicator-value-header.vehicle-list-full-indicator-status-header.vehicle-list-full-inidicator-column-full.vehicle-list-full-expand.vehicle-list-full-config.vehicle-list-filter.vehicle-details.vehicle-details-tabs.vehicle-details-tacho-tab.vehicle-details-routes-tab.vehicle-details-route-item.vehicle-details-route-stats.vehicle-details-params-tab.vehicle-details-eco-tab.vehicle-details-etoll-balance-tab.vehicle-details-header.vehicle-signal-lost.vehicle-last-updated.vehicle-current-driver.vehicle-control-indicator-value.vehicle-control-indicator-signal-value.vehicle-control-indicator-value-type.survey.profile-data-update-info-list.profile-data-update-info-details.profile-data-logged-user.profile-data-help-info.profile-data-details.common-placeholder.password-reset-confirm.password-reset.password-change.panel.notification.event-list-lite.event-list.event-list-item-info.event-list-fueI-item.event-list-area-item.event-list-alarm-item.event-list-filter.driver-list-lite.driver-list-lite-item.driver-list-lite-item-current-vehicel.driver-list-filter.driver-details.driver-details-tabs.driver-details-tacho-tab.driver-tacho-slot-details.driver-details-sms-tab.driver-details-sms-send.driver-details-sms-search.driver-details-sms-list.driver-details-eco-tab.driver-details-header.bok.auto-login.select-database.login.area-list-lite.area-list-item.area-form.area-form-input.area-list-filter.area-delete.area-create.time.tag-picker.select.search.scrolled-list.scrolled-bar.form-field.double-date-picker.date-picker.checkbox.button.component.less, .popupCustom
    const yOffset = 20; //dodatkowe przesunięcie w pionie dla niewidocznej strzałki (dodawanej przez leaflet)
    const xRightOffset = 36; //offset dla kontenera mapy po prawej stronie (z przyciskami zbliżenia/oddalenia itd); z src\app\map\controls\map.map-controls.vehicle-location.vehicle-list-lite.vehicle-list-lite-item.vehicle-list-full.vehicle-list-full-item.vehicle-list-full-tacho-header.vehicle-list-full-indicator-value-header.vehicle-list-full-indicator-status-header.vehicle-list-full-inidicator-column-full.vehicle-list-full-expand.vehicle-list-full-config.vehicle-list-filter.vehicle-details.vehicle-details-tabs.vehicle-details-tacho-tab.vehicle-details-routes-tab.vehicle-details-route-item.vehicle-details-route-stats.vehicle-details-params-tab.vehicle-details-eco-tab.vehicle-details-etoll-balance-tab.vehicle-details-header.vehicle-signal-lost.vehicle-last-updated.vehicle-current-driver.vehicle-control-indicator-value.vehicle-control-indicator-signal-value.vehicle-control-indicator-value-type.survey.profile-data-update-info-list.profile-data-update-info-details.profile-data-logged-user.profile-data-help-info.profile-data-details.common-placeholder.password-reset-confirm.password-reset.password-change.panel.notification.event-list-lite.event-list.event-list-item-info.event-list-fueI-item.event-list-area-item.event-list-alarm-item.event-list-filter.driver-list-lite.driver-list-lite-item.driver-list-lite-item-current-vehicel.driver-list-filter.driver-details.driver-details-tabs.driver-details-tacho-tab.driver-tacho-slot-details.driver-details-sms-tab.driver-details-sms-send.driver-details-sms-search.driver-details-sms-list.driver-details-eco-tab.driver-details-header.bok.auto-login.select-database.login.area-list-lite.area-list-item.area-form.area-form-input.area-list-filter.area-delete.area-create.time.tag-picker.select.search.scrolled-list.scrolled-bar.form-field.double-date-picker.date-picker.checkbox.button.component.less, .map-controls-container.upper-container
    const clusterSize = 58; //rozmiar ikony grupy pojazdów

    var itemsHeight = itemHeight * cluster.points.length;
    var containerHeight = (itemsHeight > maxContainerHeight ? maxContainerHeight : itemsHeight) + border + yOffset;

    var p = this.map!.latLngToContainerPoint(latlng);
    var mapWidth = this.map!.getPixelBounds().getSize().x!;

    if (p.y < containerHeight) {
      p.y = containerHeight;

      var sign = p.x >= mapWidth / 2 ? -1 : 1;
      p.x += sign * (containerWidth / 2 + clusterSize / 2);
    } else {
      if (p.x < containerWidth / 2) {
        p.x = containerWidth / 2 + border / 2;
      } else if (p.x + containerWidth / 2 > mapWidth - xRightOffset) {
        p.x = mapWidth - containerWidth / 2 - border / 2 - xRightOffset;
      }
    }

    return this.map!.containerPointToLatLng(p);
  }

  private addMapLayer(mapLayerType: MapLayerType) {
    switch (mapLayerType) {
      case MapLayerType.emapa:
        this.mapLayers.emapaLayer.addTo(this.map!);
        break;

      case MapLayerType.googleRoadmap:
        this.mapLayers.googleStreetsLayer.addTo(this.map!);
        break;

      case MapLayerType.googleHybrid:
        this.mapLayers.googleHybridLayer.addTo(this.map!);
        break;

      case MapLayerType.osm:
        this.mapLayers.osmLayer.addTo(this.map!);
        break;
    }
  }

  private injectGoogleMapsScript() {
    this.googleMapScriptsLoaded = true;
    const scripts = [
      // ten fragment: &callback=Function.prototype jest obejściem błędu
      // Loading the Google Maps JavaScript API without a callback is not supported
      'https://maps.googleapis.com/maps/api/js?key=AIzaSyBLr9GeO1wswmmaVsb8U0IAGUTZF0CLZKs&callback=Function.prototype',
      'https://unpkg.com/leaflet.gridlayer.googlemutant@latest/dist/Leaflet.GoogleMutant.js'
    ];
    this.javascriptInjectorService.downloadAndRunExternalScript(scripts);
  }

  private subToMapLayerChanged() {
    this.mapLayerChanged.subscribe(newMapLayerType => {
      this.authenticationService.isAuthenticated$.subscribe(isAuthenticated => {
        if (isAuthenticated) {
          const isGoogle = newMapLayerType === MapLayerType.googleRoadmap || newMapLayerType === MapLayerType.googleHybrid;
          if (isGoogle && this.authenticationService.isTekomUser && !this.googleMapScriptsLoaded)
            this.injectGoogleMapsScript();
        }
      });
    });
  }

  private subToIsRoutesTab() {
    this.vehicleUIEventsService.isRoutesTabSelected$.subscribe(isRoutesTab => {
      this.isRoutesTab = isRoutesTab;
    });
  }

  private subToChangeMinimapZoom() {
    this.mapPanelService.mainMapZoomLevelOffsetChanged.subscribe(newZoom => {
      this.mainMapZoomLevelOffset = newZoom;
      this.showPointWithZoom(this.lastCords!);
    });
  }

}
